// apps/landing-page/src/context/AuthContext.tsx
'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';

interface AuthUser {
  id: string;
  email: string | null;
  name: string | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  /** Re-checks /api/auth/me — call after an action that may change session state. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  refresh: async () => {},
});

interface MeResponse {
  authenticated: boolean;
  user?: AuthUser;
}

async function fetchMe(): Promise<MeResponse> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
    if (!res.ok) return { authenticated: false };
    return (await res.json()) as MeResponse;
  } catch {
    return { authenticated: false };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);

  // Attaches the signed-in user to every Sentry event. Without this GlitchTip stored only
  // a Cloudflare edge IP, so "how many people hit this error" was unanswerable: 455
  // hydration events looked like 18 users when that was really 18 edge nodes.
  // Id only, deliberately — it answers the count question without widening PII.
  useEffect(() => {
    Sentry.setUser(user ? { id: user.id } : null);
  }, [user?.id]);

  const refresh = useCallback(async () => {
    const me = await fetchMe();
    setIsAuthenticated(me.authenticated);
    setUser(me.authenticated ? (me.user ?? null) : null);
  }, []);

  // aks_session is httpOnly, so client JS can't read it directly — GET /api/auth/me (a
  // same-origin Next route that reads the cookie server-side) is the only source of auth
  // state here. This replaces the old cross-origin auth bridge iframe, which mobile Safari /
  // in-app webviews ITP-partitioned into always reporting signed-out (the original bug — see
  // docs/superpowers/specs/2026-07-18-session-cookie-auth-design.md).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await fetchMe();
      if (cancelled) return;
      setIsAuthenticated(me.authenticated);
      setUser(me.authenticated ? (me.user ?? null) : null);
      setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-check on tab focus / visibility change — the cookie can have been minted or cleared
  // by another tab (dashboard sign-in/logout) since this tab last checked, and there is no
  // `storage` event for an httpOnly cookie to react to instead.
  useEffect(() => {
    const handleFocusOrVisible = () => {
      if (document.visibilityState === 'hidden') return;
      refresh();
    };
    window.addEventListener('focus', handleFocusOrVisible);
    document.addEventListener('visibilitychange', handleFocusOrVisible);
    return () => {
      window.removeEventListener('focus', handleFocusOrVisible);
      document.removeEventListener('visibilitychange', handleFocusOrVisible);
    };
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
